/**
 * DiagnosisC3Table.tsx — Editable Concern / Cause / Correction (C3) diagnosis table.
 *
 * Each row captures a structured diagnosis for a work order.
 * When a DTC code is selected for a row, the Cause field auto-fills from
 * the DTC translation snapshot.
 *
 * Feature-gated: rendered only when the shop has DTC diagnosis enabled (useAiFeatureFlags, migration 195 — owner-controlled in AI settings).
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Alert,
  Spin,
  Divider,
  Row,
  Col,
} from 'antd';
import { PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useDtc } from '../../hooks/useDtc';
import type { WorkOrderDiagnosis, AttachedDtc } from '../../services/dtcService';

const { Text } = Typography;
const { TextArea } = Input;

export interface DiagnosisC3TableProps {
  workOrderId: number;
  /** Attached DTCs to enable the "linked DTC" selector — re-fetched when refreshTrigger changes */
  attachedDtcs?: AttachedDtc[];
}

interface EditRow extends Partial<WorkOrderDiagnosis> {
  _new?: boolean;
  _dirty?: boolean;
  _saving?: boolean;
}

const EMPTY_ROW: EditRow = {
  concern: '',
  cause: '',
  correction: '',
  related_dtc_code: '',
  _new: true,
  _dirty: false,
  _saving: false,
};

const DiagnosisC3Table: React.FC<DiagnosisC3TableProps> = ({
  workOrderId,
  attachedDtcs = [],
}) => {
  const { diagnoses, loadDiagnoses, createDiagnosis, updateDiagnosis } = useDtc();
  const [rows, setRows] = useState<EditRow[]>([]);
  const [addingNew, setAddingNew] = useState(false);
  const [newRow, setNewRow] = useState<EditRow>({ ...EMPTY_ROW });
  const [saveError, setSaveError] = useState<string | null>(null);

  // Load diagnoses on mount / workOrderId change
  useEffect(() => {
    loadDiagnoses(workOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId]);

  // Sync rows from server data
  useEffect(() => {
    if (diagnoses.data) {
      setRows(
        diagnoses.data.map((d) => ({
          ...d,
          _new: false,
          _dirty: false,
          _saving: false,
        }))
      );
    }
  }, [diagnoses.data]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  /** When a DTC code is selected for a row, auto-fill Cause from its translation */
  const causeFromDtc = useCallback(
    (code: string | undefined): string => {
      if (!code) return '';
      const dtc = attachedDtcs.find((d) => d.code === code);
      if (!dtc) return '';
      const likely = dtc.translation_json?.likely_causes ?? [];
      return likely.length > 0 ? likely.join('; ') : dtc.definition ?? '';
    },
    [attachedDtcs]
  );

  // ── Existing row editing ─────────────────────────────────────────────────────

  const handleExistingChange = useCallback(
    (idx: number, field: keyof WorkOrderDiagnosis, value: string) => {
      setRows((prev) => {
        const next = [...prev];
        const row = { ...next[idx], [field]: value, _dirty: true } as EditRow;
        // Auto-fill cause when DTC is selected
        if (field === 'related_dtc_code') {
          row.cause = causeFromDtc(value) || row.cause || '';
        }
        next[idx] = row;
        return next;
      });
    },
    [causeFromDtc]
  );

  const handleSaveExisting = useCallback(
    async (idx: number) => {
      const row = rows[idx];
      if (!row.id) return;
      setRows((prev) => {
        const next = [...prev];
        next[idx] = { ...next[idx], _saving: true };
        return next;
      });
      setSaveError(null);
      try {
        await updateDiagnosis(workOrderId, row.id, {
          concern: row.concern ?? '',
          cause: row.cause ?? '',
          correction: row.correction ?? '',
          related_dtc_code: row.related_dtc_code ?? undefined,
        });
        setRows((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], _dirty: false, _saving: false };
          return next;
        });
      } catch (e: any) {
        setSaveError(e?.message ?? 'Save failed');
        setRows((prev) => {
          const next = [...prev];
          next[idx] = { ...next[idx], _saving: false };
          return next;
        });
      }
    },
    [rows, workOrderId, updateDiagnosis]
  );

  // ── New row ──────────────────────────────────────────────────────────────────

  const handleNewRowChange = useCallback(
    (field: keyof EditRow, value: string) => {
      setNewRow((prev) => {
        const next = { ...prev, [field]: value };
        if (field === 'related_dtc_code') {
          next.cause = causeFromDtc(value) || prev.cause || '';
        }
        return next;
      });
    },
    [causeFromDtc]
  );

  const handleSaveNew = useCallback(async () => {
    setSaveError(null);
    setNewRow((prev) => ({ ...prev, _saving: true }));
    try {
      await createDiagnosis(workOrderId, {
        concern: newRow.concern ?? '',
        cause: newRow.cause ?? '',
        correction: newRow.correction ?? '',
        related_dtc_code: newRow.related_dtc_code || undefined,
      });
      setAddingNew(false);
      setNewRow({ ...EMPTY_ROW });
    } catch (e: any) {
      setSaveError(e?.message ?? 'Save failed');
      setNewRow((prev) => ({ ...prev, _saving: false }));
    }
  }, [workOrderId, newRow, createDiagnosis]);

  // ── Render ───────────────────────────────────────────────────────────────────

  const dtcOptions = attachedDtcs.map((d) => ({
    value: d.code,
    label: `${d.code} — ${d.definition ?? d.system ?? ''}`,
  }));

  const renderRow = (row: EditRow, idx: number, isNew: boolean) => {
    const onChange = isNew
      ? (field: keyof EditRow, v: string) => handleNewRowChange(field, v)
      : (field: keyof WorkOrderDiagnosis, v: string) => handleExistingChange(idx, field, v);

    return (
      <div
        key={isNew ? 'new' : row.id}
        style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}
        data-testid={isNew ? 'c3-new-row' : `c3-row-${row.id}`}
      >
        <Row gutter={[8, 8]}>
          {/* DTC link */}
          {dtcOptions.length > 0 && (
            <Col xs={24} sm={8}>
              <Text type="secondary" style={{ fontSize: 11 }}>Linked DTC</Text>
              <Select
                allowClear
                placeholder="Link a DTC"
                value={row.related_dtc_code || undefined}
                options={dtcOptions}
                onChange={(v: string | undefined) =>
                  onChange('related_dtc_code' as any, v ?? '')
                }
                style={{ width: '100%', marginTop: 2 }}
                size="small"
                data-testid={isNew ? 'c3-new-dtc-select' : `c3-dtc-select-${row.id}`}
              />
            </Col>
          )}

          {/* Concern */}
          <Col xs={24} sm={dtcOptions.length > 0 ? 16 : 24}>
            <Text type="secondary" style={{ fontSize: 11 }}>Concern (customer complaint)</Text>
            <TextArea
              value={row.concern ?? ''}
              onChange={(e) => onChange('concern' as any, e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ marginTop: 2 }}
              size="small"
              data-testid={isNew ? 'c3-new-concern' : `c3-concern-${row.id}`}
            />
          </Col>

          {/* Cause */}
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Cause (root cause)</Text>
            <TextArea
              value={row.cause ?? ''}
              onChange={(e) => onChange('cause' as any, e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ marginTop: 2 }}
              size="small"
              data-testid={isNew ? 'c3-new-cause' : `c3-cause-${row.id}`}
            />
          </Col>

          {/* Correction */}
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 11 }}>Correction (repair performed)</Text>
            <TextArea
              value={row.correction ?? ''}
              onChange={(e) => onChange('correction' as any, e.target.value)}
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ marginTop: 2 }}
              size="small"
              data-testid={isNew ? 'c3-new-correction' : `c3-correction-${row.id}`}
            />
          </Col>

          {/* Save button */}
          <Col xs={24} style={{ textAlign: 'right' }}>
            {isNew ? (
              <Space>
                <Button size="small" onClick={() => { setAddingNew(false); setNewRow({ ...EMPTY_ROW }); }}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  loading={row._saving}
                  onClick={handleSaveNew}
                  data-testid="c3-new-save-btn"
                >
                  Save
                </Button>
              </Space>
            ) : (
              row._dirty && (
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  loading={row._saving}
                  onClick={() => handleSaveExisting(idx)}
                  data-testid={`c3-save-btn-${row.id}`}
                >
                  Save
                </Button>
              )
            )}
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <Card
      size="small"
      title={<Text strong>Concern / Cause / Correction (C3)</Text>}
      style={{ marginTop: 8 }}
      extra={
        !addingNew && (
          <Button
            size="small"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setAddingNew(true)}
            data-testid="c3-add-btn"
          >
            Add row
          </Button>
        )
      }
      data-testid="c3-table-card"
    >
      {diagnoses.loading && <Spin style={{ display: 'block', margin: '16px auto' }} />}

      {saveError && (
        <Alert type="error" message={saveError} style={{ marginBottom: 8 }} closable onClose={() => setSaveError(null)} />
      )}

      {!diagnoses.loading && rows.length === 0 && !addingNew && (
        <Text type="secondary" data-testid="c3-empty">No diagnoses yet.</Text>
      )}

      {rows.map((row, idx) => renderRow(row, idx, false))}

      {addingNew && (
        <>
          {rows.length > 0 && <Divider style={{ margin: '8px 0' }} />}
          {renderRow(newRow, -1, true)}
        </>
      )}
    </Card>
  );
};

export default DiagnosisC3Table;
